import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grain min-h-screen grid lg:grid-cols-2 bg-[#FDFBF7]">
      <section className="relative hidden lg:flex flex-col justify-between p-14 bg-[#103080] overflow-hidden text-white">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#0090D0]/30 blur-3xl" />
        <div className="absolute -bottom-40 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#40C0E0]/20 blur-3xl" />
        <img src="/logo-mark.png" alt="" className="absolute right-10 bottom-10 w-56 opacity-10" />
        <div className="relative flex items-center gap-3">
          <img src="/logo-mark.png" alt="Rosevale" className="h-10 brightness-0 invert" />
          <span className="font-serif-brand text-xl">Rosevale Hub</span>
        </div>
        <div className="relative max-w-md">
          <p className="text-xs uppercase tracking-[0.3em] text-[#40C0E0] font-semibold mb-6">Rosevale Hub</p>
          <h1 className="font-serif-brand font-semibold text-5xl leading-[1.05]">
            A calmer week,<br /><i className="text-[#40C0E0]">planned for you.</i>
          </h1>
          <p className="mt-6 text-white/70 leading-relaxed">
            Add your client work with how long it takes. The planner fits it around your hours and meetings, and tells you what to do next.
          </p>
        </div>
        <p className="relative text-xs text-white/40">For Rosevale team members only.</p>
      </section>

      <section className="relative z-10 flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <img src="/logo-mark.png" alt="Rosevale" className="h-12 mb-8 lg:hidden" />
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Welcome back</p>
          <h2 className="font-serif-brand font-semibold text-4xl text-[#103080] mb-10">Sign in</h2>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#103080]">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" className="h-12 rounded-xl bg-white border-[#103080]/20" placeholder="you@rosevaleaccountants.co.uk" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#103080]">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" className="h-12 rounded-xl bg-white border-[#103080]/20" />
            </div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={busy} data-testid="login-submit-button"
              className="group w-full h-12 rounded-full bg-[#103080] text-white font-medium inline-flex items-center justify-center gap-2 hover:bg-[#0090D0] transition-colors duration-300 disabled:opacity-60">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <>Sign in <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" /></>}
            </button>
          </div>
          <p className="mt-8 text-xs text-[#0F172A]/50 leading-relaxed">
            No account? Ask Abbie to add you to the team. Microsoft 365 sign-in is coming soon.
          </p>
        </form>
      </section>
    </div>
  );
}
